"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  Brain,
  Sparkles,
  Crown,
  Stethoscope,
  HeartPulse,
  Activity,
  Target,
  Shield,
  Eye,
  Flame,
  Zap,
  ArrowRight,
  Mic,
  Award,
  BookOpen,
  CheckCircle2,
  Building2,
  GraduationCap,
  Heart,
  Briefcase,
  Users,
  GitBranch,
  Crosshair,
  Radio,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { StepperBlock } from "@/lib/blocks/stepper-block";
import { AccordionBlock } from "@/lib/blocks/accordion-block";
import { FeatureGridBlock } from "@/lib/blocks/feature-grid-block";

const DIAGNOSTIC_STEPS = [
  { num: "01", title: "Symptom Identification", body: "What is actually happening — not what reports indicate, not what the organization believes, but what observable evidence reveals. The presenting symptom is often not the underlying condition. Distinguishing the visible problem from the structural cause is the first clinical discipline.", icon: <Eye className="size-5" />, accent: "purple" as const },
  { num: "02", title: "Causal Analysis", body: "What is producing the symptoms. This phase distinguishes root causes from contributing factors, structural problems from behavioral symptoms, conditions that can be treated from conditions that must be managed. Treating symptoms while ignoring causes is the most common organizational error.", icon: <GitBranch className="size-5" />, accent: "red" as const },
  { num: "03", title: "Context Assessment", body: "What constraints, resources, and conditions define the environment in which the intervention must operate. The same condition requires different interventions depending on capacity, culture, and circumstances. Context determines treatment.", icon: <Target className="size-5" />, accent: "cyan" as const },
  { num: "04", title: "Intervention Design", body: "What specific action will address the root cause within the contextual constraints. This phase produces a clinical prescription — specific, measurable, and accountable — not a strategic vision or a motivational statement.", icon: <Flame className="size-5" />, accent: "emerald" as const },
  { num: "05", title: "Do-No-Harm Verification", body: "What could this intervention break, what capacity could it consume, what downstream consequences could it produce. The Hippocratic filter applied before implementation. The first restraint of clinical leadership.", icon: <Shield className="size-5" />, accent: "amber-gold" as const },
  { num: "06", title: "Outcome Accountability", body: "Did the intervention produce the intended outcome. If yes, the protocol is reinforced. If no, the failure is reviewed, the diagnosis is refined, and the system is updated. The loop closes on itself — the operating architecture evolves with every cycle.", icon: <Activity className="size-5" />, accent: "denim" as const },
];

const CLINICAL_VS_CHARISMA = [
  { title: "Error Prevention", body: "Clinical methods prevent the errors that charismatic leaders are prone to — acting before understanding, prioritizing visibility over substance, and allowing emotional states to drive decisions under pressure.", icon: <Shield className="size-5" />, accent: "purple" as const },
  { title: "Scalability", body: "Clinical methods operate regardless of who is in the room. Charismatic methods depend on the presence and performance of a single individual. The organization should not collapse when the leader departs.", icon: <Users className="size-5" />, accent: "red" as const },
  { title: "Accountability", body: "Clinical methods produce measurable outcomes that can be verified independently. Charismatic methods produce narratives that resist verification. The audience can measure the difference.", icon: <Target className="size-5" />, accent: "cyan" as const },
  { title: "Durability", body: "Clinical methods compound over time as the system learns from deviations. Charismatic methods decay when the individual departs or the attention shifts. The architecture outlasts the personality.", icon: <Award className="size-5" />, accent: "emerald" as const },
  { title: "Sovereign Validation", body: "Clinical methods have been tested for centuries in medicine, the most unforgiving environment on earth. Charismatic methods have been tested in keynote circuits, where the stakes are different. The evidence base is not comparable.", icon: <Crown className="size-5" />, accent: "amber-gold" as const },
  { title: "Cross-Domain Transfer", body: "Clinical methods transfer across industries, cultures, and contexts because the underlying discipline is universal. Charismatic methods are domain-specific and personality-bound. The transferability of the method is the evidence of its rigor.", icon: <GitBranch className="size-5" />, accent: "denim" as const },
];

const FAQ_ITEMS = [
  { q: "What is \"Leadership as Diagnosis\" about?", a: "The keynote applies the clinical method — the discipline physicians use to diagnose, triage, treat, and evaluate outcomes — to organizational leadership. Six principles drawn from medical practice and verified in sovereign-scale operations: Diagnose before treating, Triage under pressure, Run the protocol while keeping judgment, Own the outcome not the effort, Do no harm, and Regulate the room." },
  { q: "How is this different from other leadership keynotes?", a: "Most leadership keynotes draw from business schools, consulting frameworks, or executive coaching traditions. This keynote draws from medical practice and sovereign-scale operations. The clinical method has been tested for centuries in the most unforgiving environment on earth. The evidence base is structural, not rhetorical." },
  { q: "Does the keynote require medical knowledge to follow?", a: "No. The clinical method is presented as an operational framework for decision-making under uncertainty. No medical knowledge is required. Attendees from every industry — technology, government, finance, manufacturing, defense — have applied the framework directly to their organizational challenges." },
  { q: "How long is the standard keynote?", a: "Forty to fifty-five minutes for the standard keynote. Fireside chat runs thirty to forty-five minutes. Workshop and masterclass runs two to four hours. Executive briefing runs sixty to ninety minutes. Panel contribution runs twenty to thirty minutes. All formats are available virtually and in person." },
  { q: "Can the keynote be customized for executive teams?", a: "Yes. Executive briefing format applies the diagnostic framework directly to the leadership team's specific challenges. The format is confidential, invitation-only, and designed for boards, C-suites, and sovereign leadership teams who seek the clinical method as their operating framework." },
  { q: "What does the workshop format produce?", a: "The workshop applies the diagnostic framework, triage protocol, and accountability structures to specific organizational challenges. Tangible outputs include a leadership diagnostic for the organization's current state, a triage protocol for resource allocation under pressure, and accountability structures for outcome measurement. Teams leave with draft protocols ready for refinement and deployment." },
];

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
};

const SIX_PRINCIPLES = [
  {
    num: "01",
    icon: Crosshair,
    title: "Diagnose Before Treating",
    body: "The first rule of medicine. The most violated rule of leadership. A physician who prescribes without diagnosing is committing malpractice. A leader who acts before understanding is engaged in value destruction. The diagnostic phase — symptom identification, causal analysis, context assessment, intervention design — transforms organizational decision-making.",
    accent: "purple",
  },
  {
    num: "02",
    icon: Radio,
    title: "Triage Under Pressure",
    body: "The skill of ranking under fear. Not acting under pressure — prioritizing under it. When every issue screams urgency, the ability to rank is the skill that determines outcomes. Triage prevents the organizational equivalent of treating every patient as a priority: resource misallocation, attention fragmentation, and the paradox where everything is urgent and nothing receives the focus it requires.",
    accent: "red",
  },
  {
    num: "03",
    icon: Shield,
    title: "Run the Protocol, Keep the Judgment",
    body: "Protocols exist because human error is inevitable. Even the most experienced physicians make fewer errors when they follow structured protocols — not because the protocol replaces expertise, but because it protects expertise from cognitive biases, fatigue-induced lapses, and assumption-driven shortcuts that produce preventable harm.",
    accent: "cyan",
  },
  {
    num: "04",
    icon: Target,
    title: "Own the Outcome, Not the Effort",
    body: "Medicine does not credit trying. A physician who tries valiantly but loses the patient has not succeeded. The outcome is the metric. In organizations, effort-based accountability rewards hard work regardless of results. The shift to outcome accountability is the most consequential operational change an organization can make.",
    accent: "emerald",
  },
  {
    num: "05",
    icon: HeartPulse,
    title: "Do No Harm",
    body: "The oldest principle in medicine. The most underrated operational skill in leadership. Well-intentioned harm is among the most destructive patterns in organizational life. The do-no-harm assessment — what could this intervention break, what capacity could it consume, what downstream consequences could it produce — is the first filter through which every decision must pass.",
    accent: "amber-gold",
  },
  {
    num: "06",
    icon: Activity,
    title: "Regulate the Room",
    body: "The discipline of governing one&apos;s own state so that others can function. In medicine, the surgeon who panics kills the patient. In organizations, the leader who breaks under pressure breaks the team. Emotional regulation is not a soft skill. It is the operational foundation that makes all other skills possible.",
    accent: "denim",
  },
];

const KEY_ARGUMENTS = [
  { title: "Speed Without Diagnosis Is Malpractice", body: "The most dangerous leaders are the ones who act fastest. Action that precedes understanding is not leadership — it is recklessness with a title. The diagnostic discipline is the first and most violated rule.", accent: "purple" },
  { title: "Triage Is the Skill No One Teaches", body: "Ranking under fear separates leaders who survive from leaders who break. The skill can be taught, practiced, and mastered — and it must be, because the ability to prioritize under maximum pressure is the only leadership skill that scales.", accent: "red" },
  { title: "Protocols Protect Judgment", body: "Resistance to protocols is rooted in ego — the belief that experience exempts one from checklists. Evidence demonstrates the opposite. The more complex the operation, the more critical the protocol. Run the protocol. Keep the judgment.", accent: "cyan" },
  { title: "Effort Accountability Produces Mediocrity", body: "Organizations that measure effort create environments where the appearance of work is valued over the substance of outcomes. The shift to outcome accountability transforms culture from activity measurement to result measurement.", accent: "emerald" },
  { title: "First, Do No Harm", body: "The most underrated operational skill. Most leaders introduce interventions that create more damage than the problems they were designed to solve. The do-no-harm assessment is the discipline that prevents cascading consequences.", accent: "amber-gold" },
  { title: "The Leader&apos;s State Is the Team&apos;s State", body: "Emotional regulation is not a soft skill — it is operational infrastructure. The leader who breaks under pressure breaks the team. The leader who regulates under pressure enables the team to function. The discipline is learnable, practicable, and essential.", accent: "denim" },
];

const AUDIENCE = [
  { icon: Crown, title: "Leadership Development Programs", body: "Corporate leadership programs, executive education cohorts, and management development initiatives seeking a teachable, repeatable, scalable framework for leadership.", accent: "purple" },
  { icon: GraduationCap, title: "Executive Education", body: "MBA programs, executive MBA cohorts, and university leadership programs benefit from the cross-domain perspective that the physician who runs technology operations provides.", accent: "red" },
  { icon: Stethoscope, title: "Healthcare Leadership", body: "Hospital administration forums, healthcare leadership summits, and medical education conferences benefit from the direct parallel between clinical practice and organizational leadership.", accent: "cyan" },
  { icon: Users, title: "Women&apos;s Leadership", body: "Method over personality, systems over charisma, results over recognition — the clinical method aligns with the operational approach that has produced teams seventy percent female.", accent: "emerald" },
  { icon: Building2, title: "Government & Sovereign", body: "Decision-makers operating where the stakes are national. The clinical method as the operating system for governance — diagnosis, triage, protocol, outcome accountability.", accent: "amber-gold" },
  { icon: Briefcase, title: "Corporate Retreats", body: "Private gatherings of executive teams seeking to transform their operating method through the clinical framework — with workshop extensions available for deeper implementation.", accent: "denim" },
];

const TAKEAWAYS = [
  "Diagnosis before action is non-negotiable. Every leadership failure traces to a moment where action preceded understanding.",
  "Triage is the leadership skill no one teaches. The ability to rank competing priorities under fear is what separates leaders who survive from leaders who break.",
  "Protocols protect judgment; they do not replace it. Run the protocol. Keep the judgment. The resistance to protocols is rooted in ego, not in evidence.",
  "Outcome accountability produces results; effort accountability produces activity. The shift from rewarding effort to rewarding outcomes is the most consequential change an organization can make.",
  "The leader&apos;s state determines the team&apos;s capacity. Emotional regulation is not a soft skill. It is the operational foundation that makes all other skills possible.",
];

const GAINS = [
  "The diagnostic approach to organizational assessment — diagnose before treating, form a complete picture before intervening",
  "Triage as a leadership discipline — the skill of ranking under pressure, the protocol for resource allocation under constraint",
  "Protocol-driven leadership — why protocols outperform individual judgment at scale and how to implement them without stifling innovation",
  "Outcome accountability structures — the specific mechanisms that hold processes accountable for results, not effort",
  "The do-no-harm assessment — how to evaluate the potential damage of every leadership intervention before implementing it",
  "Emotional regulation under pressure — the specific practices that sustain composure so the team can function when the stakes are highest",
];

const FORMATS = [
  { label: "Standard Keynote", duration: "40–55 min", body: "Clinical leadership education structured from core argument through the six principles to the diagnostic framework and actionable takeaways.", accent: "purple" },
  { label: "Fireside Chat", duration: "30–45 min", body: "Conversational depth for intimate leadership gatherings, board retreats, and executive education sessions.", accent: "red" },
  { label: "Workshop", duration: "2–4 hrs", body: "Deep-dive clinical leadership training applying the diagnostic framework, triage protocol, and accountability structures to specific organizational challenges.", accent: "cyan" },
  { label: "Executive Briefing", duration: "60–90 min", body: "Private, invitation-only session for C-suite teams seeking to adopt the clinical method as their operating framework.", accent: "emerald" },
  { label: "Panel Contribution", duration: "20–30 min", body: "Contribution to multi-speaker discussions on leadership, operations, governance, or organizational performance.", accent: "amber-gold" },
  { label: "Virtual Delivery", duration: "All formats", body: "All formats available for virtual events with the same operational authority and engagement quality.", accent: "denim" },
];

export default function LeadershipAsDiagnosisPage() {
  return (
    <>
      <PageHero
        icon={Brain}
        overline="Signature Keynote"
        overlineClassName="text-purple"
        title={
          <>
            Leadership Is Not Charisma. <span className="text-gradient-premium">It Is Clinical.</span>
          </>
        }
        subtitle="Leadership as Diagnosis applies the clinical method — the discipline physicians use to diagnose, triage, treat, and evaluate outcomes — to organizational leadership. Six principles that every leader must master, drawn from a physician&apos;s training and a decade of sovereign-scale operations. The audience leaves with operational protocols, not abstract ideals."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Speaking", href: "/speaking/" }, { label: "Leadership as Diagnosis" }]}
        badge="The clinical method applied"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-5`}>
            <Sparkles className="size-3.5 text-purple" />
            <span className="text-overline text-purple">Abstract</span>
          </div>
          <h2 className="text-display-md mb-5">What medicine teaches about running organizations.</h2>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            The medical profession has spent centuries refining a method for making decisions under uncertainty, under pressure, with incomplete information, and with consequences that cannot be reversed. That method is the most transferable leadership framework in existence. Yet it is almost never taught in leadership programs, business schools, or executive education.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed">
            The keynote bridges that gap. The MBBS in Gynaecology from Delhi provides the clinical foundation. The decade building and running operations across eighteen countries provides the operational proving ground. The experience leading teams over seventy percent women provides the evidence that the clinical method works — not in theory, but in the most demanding operational conditions on earth.
          </p>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-4`}>
            <Target className="size-3.5 text-red" />
            <span className="text-overline text-red">The Core Argument</span>
          </div>
          <h2 className="text-display-md mb-4">Leadership is clinical, not charismatic.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The central argument dismantles one of the most persistent myths in modern leadership education: that leadership is primarily a function of charisma, vision, and inspirational communication. The clinical method — not the keynote — is what scales.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {KEY_ARGUMENTS.map((k, i) => {
            const a = ACCENT[k.accent];
            return (
              <motion.div
                key={k.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group relative glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all overflow-hidden`}
              >
                <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                <div className="relative">
                  <div className="flex items-start gap-3 mb-3">
                    <div className={`size-9 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                      <Flame className="size-4 text-white" />
                    </div>
                    <h3 className={`text-heading-sm ${a.text}`} dangerouslySetInnerHTML={{ __html: k.title }} />
                  </div>
                  <p className="text-caption text-muted-foreground leading-relaxed">{k.body}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <Stethoscope className="size-3.5 text-purple" />
            <span className="text-overline text-purple">The Six Clinical Principles</span>
          </div>
          <h2 className="text-display-md mb-4">The operating system for leadership under pressure.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Six principles — each drawn from medical practice, each proven in sovereign-scale operations, each immediately applicable to organizational leadership. Together, they form the operating system for leaders who must function where failure is not an option.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {SIX_PRINCIPLES.map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.div
                key={p.num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`group relative glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all overflow-hidden`}
              >
                <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                <div className="relative">
                  <div className="flex items-start gap-4 mb-3">
                    <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center shadow-lg ring-2 ${a.ring} shrink-0`}>
                      <p.icon className="size-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`text-caption font-bold ${a.text} opacity-60 mb-1`}>Principle {p.num}</div>
                      <h3 className={`text-heading-md ${a.text}`}>{p.title}</h3>
                    </div>
                  </div>
                  <p className="text-body-sm text-muted-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: p.body }} />
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-5`}>
              <Stethoscope className="size-3.5 text-cyan" />
              <span className="text-overline text-cyan">The Delivery Room Parallel</span>
            </div>
            <h2 className="text-display-md mb-4">From medical practice to boardroom.</h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              In the delivery room, the physician faces conditions that mirror organizational leadership: urgency, incomplete information, competing priorities, and consequences that cannot be reversed. The physician must diagnose in real time, triage competing demands, follow protocols while maintaining clinical judgment, own the outcome regardless of effort, do no harm under pressure, and regulate the emotional state so the team can function.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              These are not metaphors. They are the actual conditions under which the operating method was developed. The delivery room trained the discipline. The boardroom tested it. The evidence confirms it. The keynote transfers this method to the audience&apos;s organizational context with specificity, precision, and immediate applicability.
            </p>
          </div>
          <div className="space-y-3">
            {[
              { phase: "Before Action", body: "Form a complete picture. Symptom identification, causal analysis, context assessment, intervention design. No action without diagnosis.", accent: "purple" },
              { phase: "During Pressure", body: "Rank under fear. Allocate resources under constraint. Make the decisions no one else in the room can make. Speed is irrelevant without prioritization.", accent: "red" },
              { phase: "Through Execution", body: "Run the protocol while preserving judgment. Standardize the repeatable so creativity applies to the critical. Protect expertise from cognitive failure modes.", accent: "cyan" },
              { phase: "After Outcome", body: "Own the result, not the effort. Medicine does not credit trying. The patient lived or did not. The same discipline applied to organizations transforms culture.", accent: "emerald" },
            ].map((p, i) => {
              const a = ACCENT[p.accent];
              return (
                <motion.div
                  key={p.phase}
                  initial={{ opacity: 0, x: 16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`size-9 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                      <span className="text-caption font-bold text-white">{i + 1}</span>
                    </div>
                    <div>
                      <h3 className={`text-heading-sm mb-1 ${a.text}`}>{p.phase}</h3>
                      <p className="text-caption text-muted-foreground leading-relaxed">{p.body}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <CheckCircle2 className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">Key Takeaways</span>
          </div>
          <h2 className="text-display-md mb-4">Five insights implemented tomorrow.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The minimum viable operating system for clinical leadership. Specific, implementable actions that transform leadership practice starting the next day.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-3 max-w-5xl mx-auto">
          {TAKEAWAYS.map((t, i) => {
            const accents = ["purple", "red", "cyan", "emerald", "amber-gold"];
            const a = ACCENT[accents[i % accents.length]];
            return (
              <motion.div
                key={t}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="flex items-start gap-3">
                  <div className={`size-9 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <span className="text-caption font-bold text-white">{i + 1}</span>
                  </div>
                  <p className="text-body-sm text-muted-foreground leading-relaxed flex-1" dangerouslySetInnerHTML={{ __html: t }} />
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <Users className="size-3.5 text-denim" />
            <span className="text-overline text-denim">Who This Keynote Serves</span>
          </div>
          <h2 className="text-display-md mb-4">Ideal audience.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Designed for audiences operating under conditions where leadership failures produce measurable consequences — where the margin for error is zero and the stakes are organizational, financial, or national.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {AUDIENCE.map((a, i) => {
            const ac = ACCENT[a.accent];
            return (
              <motion.div
                key={a.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${ac.bg} to-transparent border ${ac.border} hover:scale-[1.01] transition-all`}
              >
                <div className="flex items-start gap-3">
                  <div className={`size-10 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <a.icon className="size-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-heading-sm mb-1.5 ${ac.text}`} dangerouslySetInnerHTML={{ __html: a.title }} />
                    <p className="text-caption text-muted-foreground leading-relaxed">{a.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.amber_gold?.pill || ACCENT["amber-gold"].pill} mb-5`}>
              <Award className="size-3.5 text-amber-gold" />
              <span className="text-overline text-amber-gold">What Attendees Gain</span>
            </div>
            <h2 className="text-display-md mb-4">Actionable clinical leadership skills.</h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              The audience equips with a specific, actionable skill set — not theoretical frameworks, but operational protocols that can be implemented the next day. Every principle is presented with enough specificity that attendees can begin applying it immediately to their leadership challenges.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              The clinical method is not a personality trait. It is a learnable, practicable, and scalable operating system — one that produces outcomes at sovereign scale precisely because it demands the rigor that sovereign-scale operations require.
            </p>
          </div>
          <div className="space-y-3">
            {GAINS.map((g, i) => {
              const accents = ["purple", "red", "cyan", "emerald", "amber-gold", "denim"];
              const a = ACCENT[accents[i % accents.length]];
              return (
                <motion.div
                  key={g}
                  initial={{ opacity: 0, x: 16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className={`glass-card rounded-xl p-4 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`size-8 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                      <CheckCircle2 className="size-4 text-white" />
                    </div>
                    <p className="text-caption text-muted-foreground leading-relaxed flex-1">{g}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-purple/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-red/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Sparkles className="size-10 text-purple mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Book this keynote.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Leadership as Diagnosis is the clinical foundation of the speaking portfolio. The skills that sustain excellence under pressure — drawn from the delivery room, applied to the boardroom, proven across sovereign-scale operations.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Book the Keynote
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/speaking/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <BookOpen className="size-4" />
                All Speaking Topics
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <StepperBlock
          steps={DIAGNOSTIC_STEPS}
          orientation="vertical"
          eyebrow="Signature Framework"
          title="The Diagnostic Protocol Steps."
          description="Six stages of the organizational diagnostic protocol. Each builds on the previous one. Each prevents the premature action that produces organizational harm. The protocol is drawn from clinical practice and refined across sovereign-scale operations."
        />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.amber_gold?.pill || ACCENT["amber-gold"].pill} mb-4`}>
            <Crown className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">Method Over Personality</span>
          </div>
          <h2 className="text-display-md mb-4">Why clinical leadership outperforms charismatic leadership.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Three mechanisms through which clinical discipline produces outcomes that personality-driven leadership cannot replicate. The evidence is drawn from sovereign-scale operations where the consequences of failure are national.
          </p>
        </div>
        <FeatureGridBlock features={CLINICAL_VS_CHARISMA} columns={3} />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-5`}>
            <Flame className="size-3.5 text-red" />
            <span className="text-overline text-red">The Evidence</span>
          </div>
          <h2 className="text-display-md mb-4">Clinical leadership at sovereign scale.</h2>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            The argument is not rhetorical. It is structural. The operational metrics that define the track record are the outcomes of clinical leadership — method-driven, protocol-based, outcome-accountable, and delivered with the precision that the medical profession has refined over centuries.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            Fifteen out of eighteen political campaigns won. Over nine hundred million voters reached. One-hundred-percent client satisfaction. A ninety-nine-point-nine-nine-nine-nine percent operational uptime. Zero security incidents across fifteen-plus years of sovereign-scale operations. These are not the outcomes of charismatic leadership. They are the outcomes of clinical leadership.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed">
            Three mechanisms explain the difference. First, error prevention: clinical methods prevent the errors that charismatic leaders are prone to. Second, scalability: clinical methods operate regardless of who is in the room. Third, accountability: clinical methods produce measurable outcomes that can be verified independently. The difference is operational, not stylistic — and the evidence is structural, not narrative.
          </p>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <AccordionBlock
          items={FAQ_ITEMS}
          eyebrow="Frequently Asked Questions"
          title="What decision-makers ask before booking."
          description="The questions that surface most often in preliminary conversations. Each answer reflects the operating reality, not the marketing narrative."
        />
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "How Excellence Actually Runs", description: "The system that clinical leadership sustains — protocols, checklists, reviews, outcome accountability.", href: "/speaking/how-excellence-actually-runs/", icon: Stethoscope, accent: "red" },
          { title: "The 70% Project", description: "The talent strategy that clinical method builds — majority-female teams that outperform on five measurable dimensions.", href: "/speaking/the-70-percent-project/", icon: Heart, accent: "emerald" },
          { title: "Build in the Dark", description: "The discipline that sustains clinical leadership under hostile public conditions.", href: "/speaking/build-in-the-dark/", icon: Eye, accent: "denim" },
          { title: "The Triple Threat", description: "The cross-domain integration that clinical method enables across medicine, technology, and politics.", href: "/speaking/the-triple-threat/", icon: GitBranch, accent: "amber-gold" },
        ]}
      />
    </>
  );
}
